import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import { renderSchoolLogo } from '../components/SchoolPicker';
import UserPickerModal from '../components/UserPickerModal';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 0',
  borderBottom: '1px solid #f3f4f6',
};

const COL_BASE = { minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' };
const COL_NAME = { ...COL_BASE, flex: '1 1 200px', fontFamily: '"Baikal Book"', fontSize: '15px' };
const COL_EMAIL = { ...COL_BASE, flex: '1 1 240px', fontFamily: '"Baikal Book"', fontSize: '14px', color: '#4b5563' };
const COL_SCHOOL = { ...COL_BASE, flex: '0 0 32px', display: 'flex', alignItems: 'center' };
const COL_META = { ...COL_BASE, flex: '0 0 120px', fontFamily: '"Baikal Book"', fontSize: '13px', color: '#6b7280' };
const ROW_ACTION = { flexShrink: 0, display: 'flex', gap: '8px' };

function displayName(user) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

const SuperuserTestFreeEntries = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [raffle, setRaffle] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const applyPayload = (data) => {
    setRaffle(data?.raffle || null);
    setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
  };

  const loadPage = useCallback(async () => {
    const data = await apiService.getTestFreeEntriesSuperuser();
    applyPayload(data);
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        await loadPage();
      } catch (err) {
        console.error('Failed to load test free entries:', err);
        setError('Could not load test free entries.');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [loadPage, navigate]);

  const saveList = async (nextRecipients, successMessage) => {
    setError('');
    setNotice('');
    const data = await apiService.saveTestFreeEntriesSuperuser({
      user_ids: nextRecipients.map((user) => user.id),
    });
    applyPayload(data);
    if (successMessage) setNotice(successMessage);
  };

  const addUsers = async (picked) => {
    const listed = new Set(recipients.map((user) => String(user.id)));
    const fresh = picked.filter((user) => !listed.has(String(user.id)));
    if (!fresh.length) {
      setPickerOpen(false);
      return;
    }
    setAddingUsers(true);
    try {
      await saveList(
        [...recipients, ...fresh],
        fresh.length === 1
          ? `${displayName(fresh[0])} saved to the list.`
          : `${fresh.length} people saved to the list.`,
      );
      setPickerOpen(false);
    } catch (err) {
      console.error('Failed to add users:', err);
      setError(err?.response?.data?.error || 'Could not save these users.');
    } finally {
      setAddingUsers(false);
    }
  };

  const removeUser = async (user) => {
    setBusyId(user.id);
    try {
      await saveList(
        recipients.filter((row) => String(row.id) !== String(user.id)),
        `${displayName(user)} removed from the list.`,
      );
    } catch (err) {
      console.error('Failed to remove user:', err);
      setError(err?.response?.data?.error || 'Could not remove this user.');
    } finally {
      setBusyId(null);
    }
  };

  const sendOne = async (user) => {
    setBusyId(user.id);
    setError('');
    setNotice('');
    try {
      const data = await apiService.sendTestFreeEntriesSuperuser({ user_id: user.id });
      applyPayload(data);
      setNotice(`Sent a claim prompt to ${displayName(user)}.`);
    } catch (err) {
      console.error('Failed to send test entry:', err);
      setError(err?.response?.data?.error || 'Could not send this claim.');
    } finally {
      setBusyId(null);
    }
  };

  const sendAll = async () => {
    setSendingAll(true);
    setError('');
    setNotice('');
    try {
      const data = await apiService.sendTestFreeEntriesSuperuser({ all: true });
      applyPayload(data);
      setNotice('Sent a claim prompt to everyone on the list.');
    } catch (err) {
      console.error('Failed to send all test entries:', err);
      setError(err?.response?.data?.error || 'Could not send to the list.');
    } finally {
      setSendingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const cover = raffle?.images?.[0]?.image_path;

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" text="Back to Superuser Dashboard" />
          <h1 className="staff-page-header">Send Test Free Entries</h1>
          <p className="staff-page-subheader">
            Save who should receive the Sideline Night Pass claim. Send does not grant an entry —
            they claim it in the app, and each claim adds one entry for the draw.
          </p>

          {raffle && (
            <div className="add-reward-section" style={{ marginTop: '32px' }}>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {cover && (
                  <img
                    src={cover}
                    alt=""
                    style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 12, background: '#111' }}
                  />
                )}
                <div style={{ flex: '1 1 280px' }}>
                  <h2 className="staff-section-header" style={{ marginBottom: '8px' }}>{raffle.title}</h2>
                  <p className="staff-page-content" style={{ margin: '0 0 12px' }}>{raffle.description}</p>
                  <p className="staff-page-content" style={{ margin: 0 }}>
                    Drawing {formatDateTime(raffle.drawing_date)} · {raffle.total_entries || 0} entries
                  </p>
                  <button
                    type="button"
                    className="staff-page-button"
                    style={{ marginTop: '16px' }}
                    onClick={() => navigate(`/staff/raffles/${raffle.id}/draw/`)}
                  >
                    <span className="staff-page-button-text">Draw this raffle</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="add-reward-section" style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <h2 className="staff-section-header" style={{ margin: 0 }}>Recipients</h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="staff-page-button"
                  onClick={() => setPickerOpen(true)}
                >
                  <span className="staff-page-button-text">Add people</span>
                </button>
                <button
                  type="button"
                  className="staff-page-button"
                  onClick={sendAll}
                  disabled={sendingAll || recipients.length === 0}
                >
                  <span className="staff-page-button-text">
                    {sendingAll ? 'Sending...' : 'Send to all'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <p className="staff-page-content" style={{ margin: '24px 0 8px' }}>
            {recipients.length === 1 ? '1 person on the list' : `${recipients.length} people on the list`}
          </p>
          {recipients.length === 0 && (
            <p className="staff-page-content">Nobody is on the list yet.</p>
          )}
          {recipients.map((user) => (
            <div key={user.id} style={ROW}>
              <div style={COL_NAME}>{displayName(user)}</div>
              <div style={COL_EMAIL}>{user.email}</div>
              <div style={COL_SCHOOL} title={user.active_school?.name || ''}>
                {user.active_school ? renderSchoolLogo(user.active_school, 28) : '—'}
              </div>
              <div style={COL_META}>
                {user.pending_count || 0} waiting · {user.entry_count || 0} entries
              </div>
              <div style={ROW_ACTION}>
                <button
                  type="button"
                  className="staff-page-button"
                  onClick={() => sendOne(user)}
                  disabled={busyId === user.id}
                >
                  <span className="staff-page-button-text">
                    {busyId === user.id ? 'Sending...' : 'Send'}
                  </span>
                </button>
                <button
                  type="button"
                  className="dashboard-button"
                  style={{ minWidth: 0, padding: '8px 16px' }}
                  onClick={() => removeUser(user)}
                  disabled={busyId === user.id}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {error && (
            <div style={{
              marginTop: '16px',
              padding: '10px 14px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
          {notice && (
            <div style={{
              marginTop: '16px',
              padding: '10px 14px',
              backgroundColor: '#d1fae5',
              border: '1px solid #a7f3d0',
              borderRadius: '8px',
              color: '#065f46',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
            }}>
              {notice}
            </div>
          )}
          {pickerOpen && (
            <UserPickerModal
              isOpen
              title="Add people"
              excludeIds={recipients.map((user) => user.id)}
              confirming={addingUsers}
              onClose={() => {
                if (!addingUsers) setPickerOpen(false);
              }}
              onConfirm={addUsers}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserTestFreeEntries;
